import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";
import APIAgentExecutionsSub from "./agent-executions/client";
import APIAIMonitoringSub from "./ai-monitoring/client";
import APIMonitoringSub from "./monitoring/client";
import APIAppleSMSSub from "./apple-sms/client";
import APIAudioFilesSub from "./audio/client";
import APIAuthSub from "./auth/client";
import APIBuyingPatternsSub from "./buying-patterns/client";
import APIChatSessionsSub from "./chat-sessions/client";
import APIChatSharesSub from "./chat-shares/client";
import APICompaniesSub from "./companies/client";
import APIDocumentsSub from "./documents/client";
import APIEmailAccountsSub from "./email_accounts/client";
import APIEmailContactsSub from "./email_contacts/client";
import APIEmailsSub from "./emails/client";
import APIEmployeesSub from "./employees/client";
import APIExportsSub from "./exports/client";
import APIFeatureFlagsSub from "./feature-flags/client";
import APIFeesSub from "./fees/client";
import APIFlowsSub from "./flows/client";
import APIGroupsSub from "./groups/client";
import APILocationsSub from "./locations/client";
import APILoginSub from "./login/client";
import APILotsSub from "./lots/client";
import APIMessagesSub from "./messages/client";
import APIOffersSub from "./offers/client";
import APIOperationsSub from "./operations/client";
import APIOrdersSub from "./orders/client";
import APIPartnerMenuSub from "./partner-menu/client";
import APIPartnerPreferencesSub from "./partner-preferences/client";
import APIPartnerEventsSub from "./partner_events/client";
import APIPartnersSub from "./partners/client";
import APIPreOrderFlowsSub from "./pre-order-flows/client";
import APIPreOrdersSub from "./pre_orders/client";
import APIPricingSub from "./pricing/client";
import APIPrintJobsSub from "./print-jobs/client";
import APIProductRelationshipsSub from "./product-relationships/client";
import APIProductsSub from "./products/client";
import APIErrorFeedbackSub from "./error-feedback/client";
import APIReportsSub from "./reports/client";
import APIRoutesSub from "./routes/client";
import APISeedingSub from "./seeding/client";
import APISMSManagementSub from "./sms-management/client";
import APISMSSub from "./sms/client";
import APISMSContactsSub from "./sms_contacts/client";
import APIStocksSub from "./stocks/client";
import APIStorageSub from "./storage/client";
import APITagsSub from "./tags/client";
import APITransactionsSub from "./transactions/client";
import APITransfersSub from "./transfers/client";
import APIUnitsSub from "./units/client";
import APIUserAISub from "./user_ai/client";
import APIUserPartnersSub from "./user-partners/client";
import APIUsersSub from "./users/client";
import APIWhatsAppSub from "./whatsapp/client";
import APIWorkflowsSub from "./workflows/client";
import APISuperAdminSub from "./superadmin/client";

export default class APIGenerated extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  users = new APIUsersSub(this._client);
  orders = new APIOrdersSub(this._client);
  companies = new APICompaniesSub(this._client);
  employees = new APIEmployeesSub(this._client);
  products = new APIProductsSub(this._client);
  productRelationships = new APIProductRelationshipsSub(this._client);
  groups = new APIGroupsSub(this._client);
  // `clientGroups` removed — client-group lifecycle moved to V2 push-batch.
  // See `useClientGroupMutations` in `@stockline/web` and the wrapper in
  // `frontend/src/query/hooks/useClientGroups.ts`.
  messages = new APIMessagesSub(this._client);
  offers = new APIOffersSub(this._client);
  stocks = new APIStocksSub(this._client);
  partners = new APIPartnersSub(this._client);
  partnerEvents = new APIPartnerEventsSub(this._client);
  buyingPatterns = new APIBuyingPatternsSub(this._client);
  partnerPreferences = new APIPartnerPreferencesSub(this._client);
  flows = new APIFlowsSub(this._client);
  locations = new APILocationsSub(this._client);
  login = new APILoginSub(this._client);
  auth = new APIAuthSub(this._client);
  whatsapp = new APIWhatsAppSub(this._client);
  preOrders = new APIPreOrdersSub(this._client);
  preOrderFlows = new APIPreOrderFlowsSub(this._client);
  emails = new APIEmailsSub(this._client);
  emailAccounts = new APIEmailAccountsSub(this._client);
  emailContacts = new APIEmailContactsSub(this._client);
  audio = new APIAudioFilesSub(this._client);
  sms = new APISMSSub(this._client);
  appleSMS = new APIAppleSMSSub(this._client);
  smsManagement = new APISMSManagementSub(this._client);
  smsContacts = new APISMSContactsSub(this._client);
  userAI = new APIUserAISub(this._client);
  workflows = new APIWorkflowsSub(this._client);
  storage = new APIStorageSub(this._client);
  featureFlags = new APIFeatureFlagsSub(this._client);
  units = new APIUnitsSub(this._client);
  agentExecutions = new APIAgentExecutionsSub(this._client);
  chatShares = new APIChatSharesSub(this._client);
  chatSessions = new APIChatSessionsSub(this._client);
  printJobs = new APIPrintJobsSub(this._client);
  exports = new APIExportsSub(this._client);
  lots = new APILotsSub(this._client);
  inventoryOperations = new APIOperationsSub(this._client);
  inventoryTransactions = new APITransactionsSub(this._client);
  inventoryTransfers = new APITransfersSub(this._client);
  partnerMenu = new APIPartnerMenuSub(this._client);
  aiMonitoring = new APIAIMonitoringSub(this._client);
  monitoring = new APIMonitoringSub(this._client);
  documents = new APIDocumentsSub(this._client);
  fees = new APIFeesSub(this._client);
  reports = new APIReportsSub(this._client);
  routes = new APIRoutesSub(this._client);
  errorFeedback = new APIErrorFeedbackSub(this._client);
  seeding = new APISeedingSub(this._client);
  tags = new APITagsSub(this._client);
  pricing = new APIPricingSub(this._client);
  superAdmin = new APISuperAdminSub(this._client);
  userPartners = new APIUserPartnersSub(this._client);

  postUsers = async ({ ...body }: types.UserCreate): Promise<types.Token> => {
    let res = await this._fetch({
      url: `/users/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postOrders = async ({
    ...body
  }: types.OrderCreate): Promise<types.OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getOrders = async ({
    page,
    size,
  }: {
    page?: number | null;
    size?: number | null;
  } = {}): Promise<types.PageOrderResponse> => {
    let res = await this._fetch({
      url: `/orders/`,
      method: "GET",
      params: { page: page, size: size },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getCompanies = async (): Promise<types.CompanyResponse[]> => {
    let res = await this._fetch({
      url: `/companies/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putCompanies = async ({
    ...body
  }: types.CompanyUpdate): Promise<types.CompanyResponse> => {
    let res = await this._fetch({
      url: `/companies/`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postCompanies = async ({
    ...body
  }: types.CompanyCreate): Promise<types.CompanyWithTokenResponse> => {
    let res = await this._fetch({
      url: `/companies/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteCompanies = async (): Promise<{
    message: string;
    access_token?: string;
    token_type?: string;
  }> => {
    let res = await this._fetch({
      url: `/companies/`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getGroups = async (): Promise<types.GroupResponse[]> => {
    let res = await this._fetch({
      url: `/groups/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postGroups = async ({
    ...body
  }: types.GroupCreate): Promise<types.GroupResponse> => {
    let res = await this._fetch({
      url: `/groups/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getOffers = async (): Promise<types.OfferResponse[]> => {
    let res = await this._fetch({
      url: `/offers/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postOffers = async ({
    ...body
  }: types.OfferRequest): Promise<types.OfferRequest> => {
    let res = await this._fetch({
      url: `/offers/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getStocks = async ({
    productId,
    supplierId,
    flowId,
    userId,
    page,
    size,
  }: {
    productId?: UUID | null;
    supplierId?: UUID | null;
    flowId?: UUID | null;
    userId?: UUID | null;
    page?: number | null;
    size?: number | null;
  } = {}): Promise<types.Page<types.StockResponse>> => {
    let res = await this._fetch({
      url: `/stocks/`,
      method: "GET",
      params: {
        product_id: productId,
        supplier_id: supplierId,
        flow_id: flowId,
        user_id: userId,
        page: page,
        size: size,
      },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const data: types.PageStockResponse = await res.json();
      // Transform to match Page<T> interface expected by usePaged
      return {
        items: data.items,
        page: data.page,
        page_size: data.size,
        total_pages: data.pages,
        total_items: data.total,
      };
    }
    throw new Error("Bad content type");
  };

  postStocks = async ({
    ...body
  }: types.StockCreate): Promise<types.StockResponse> => {
    let res = await this._fetch({
      url: `/stocks/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getPartners = async (): Promise<types.PartnerResponse[]> => {
    let res = await this._fetch({
      url: `/partners/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const response = await res.json();
      // Handle paginated response
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      // Fallback for direct array response (backward compatibility)
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    }
    throw new Error("Bad content type");
  };

  postPartners = async ({
    ...body
  }: types.PartnerCreate): Promise<types.PartnerResponse> => {
    let res = await this._fetch({
      url: `/partners/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getFlows = async ({
    filter,
    page,
    size,
  }: {
    filter: types.FlowFilter;
    page?: number | null;
    size?: number | null;
  }): Promise<types.PageFlowResponse> => {
    // Build flat query params from filter object (backend expects flat params, not nested object)
    const params: Record<string, any> = {
      page: page || 1,
      size: size || 50,
    };
    if (filter.product_id) params.product_id = filter.product_id;
    if (filter.partner_id) params.partner_id = filter.partner_id;
    if (filter.stock_id) params.stock_id = filter.stock_id;
    if (typeof filter.provisional === "boolean")
      params.provisional = filter.provisional;
    if (filter.flow_types && filter.flow_types.length > 0)
      params.flow_types = filter.flow_types;
    if (filter.created_at_s) params.created_at_s = filter.created_at_s;
    if (filter.created_at_e) params.created_at_e = filter.created_at_e;

    let res = await this._fetch({
      url: `/flows/`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getHealthCheck = async (): Promise<any> => {
    let res = await this._fetch({
      url: `/health-check`,
      method: "GET",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postToken = async ({
    ...body
  }: types.BodyLoginForAccessTokenTokenPost): Promise<types.Token> => {
    let res = await this._fetch({
      url: `/token`,
      method: "POST",
      body: body,
      bodyMime: "application/x-www-form-urlencoded",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postContactDemo = async ({
    ...body
  }: types.BodyContactDemoPost): Promise<types.ContactDemoResponse> => {
    let res = await this._fetch({
      url: `/contact-demo`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
